import { TranslateModule } from '@ngx-translate/core';
import { AuthModule, LogLevel } from 'angular-auth-oidc-client';

import { PlatformModule } from '@angular/cdk/platform';
import {
  LOCALE_ID,
  ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf,
} from '@angular/core';
import {
  AngularFireModule,
  FIREBASE_APP_NAME,
  FIREBASE_OPTIONS,
} from '@angular/fire/compat';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MAT_LEGACY_FORM_FIELD_DEFAULT_OPTIONS as MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/legacy-form-field';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';

import { AuthModuleConfigId, MwAuthModule } from './auth';
import { DevOptionsModule } from './components/dev-options/dev-options.module';
import { MW_APP_CONFIG, MwAppConfigService } from './config';
import { Config } from './config';
import { buildDefaultConfig } from './config/config-builder.service';

import { MwErrorHandlerModule } from './error-handler';
import { MwMonitorService } from './error-handler/monitor.service';
import { MwI18nModule } from './i18n/i18n.module';
import {
  MwCoreDateTimeService,
  MwIconLoaderService,
  MwLocaleService,
  MwNotificationService,
  MwPlatformService,
} from './services';
import { MwCoreStoreModule } from './store';
import { environment } from '../environments/environment';

export function localeFactory(localeService: MwLocaleService): MwLocaleService {
  localeService.registerLocales();
  return localeService;
}

@NgModule({
  imports: [
    TranslateModule,
    PlatformModule,
    MatSnackBarModule,
    MatDialogModule,
    AngularFireModule,
    AngularFireMessagingModule,
    MwCoreStoreModule,
    MwI18nModule,
    MwAuthModule,
    MwErrorHandlerModule,
    DevOptionsModule,
  ],
})
export class MwCoreModule {
  constructor(
    @Optional() @SkipSelf() parentModule: MwCoreModule,
    readonly monitorService: MwMonitorService
  ) {
    if (parentModule) {
      throw new Error(
        'CoreModule is already loaded. Import it in the AppModule only!'
      );
    }
  }

  static auth(config: Config): ModuleWithProviders<AuthModule> {
    const mwConfig = {
      configId: AuthModuleConfigId.MobileWorker,
      authority: `${
        new URL(
          config.environment.tokenEndpoint || 'https://sts-sit.mworker.com'
        ).origin
      }`,
      redirectUrl: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      clientId: config.environment.clientId,
      scope: 'openid profile api offline_access',
      responseType: 'code',
      silentRenew: true,
      useRefreshToken: true,
      logLevel: LogLevel.Error,
      unauthorizedRoute: '/unauthorized',
      customParamsAuthRequest: {
        version: config.environment.version || 0,
      },
      customParamsCodeRequest: {
        client_secret: config.environment.clientSecret,
      },
      customParamsRefreshTokenRequest: {
        client_secret: config.environment.clientSecret,
      },
    };

    return AuthModule.forRoot({
      config: [
        { ...mwConfig },
        {
          ...mwConfig,
          configId: AuthModuleConfigId.MobileWorkerLocal,
          authority: `${
            new URL(
              config.environment.localTokenEndpoint || 'https://localhost:44305'
            ).origin
          }`,
        },
        {
          configId: AuthModuleConfigId.DevincoConnect,
          authority: `${
            new URL(
              config.environment.devincoConnectTokenEndpoint ||
                'https://connect-stage.devinco.com'
            ).origin
          }`,
          redirectUrl: window.location.origin,
          postLogoutRedirectUri: window.location.origin,
          clientId:
            config.environment.devincoConnectClientId ??
            config.environment.clientId,
          scope: 'openid profile offline_access',
          responseType: 'code',
          silentRenew: true,
          useRefreshToken: true,
          logLevel: LogLevel.Error,
          unauthorizedRoute: '/unauthorized',
        },
      ],
    });
  }

  static forRoot(config: Config): ModuleWithProviders<MwCoreModule> {
    const localConfig = buildDefaultConfig(config, environment);
    environment.production = config.environment.production === true;

    if (config.environment?.componentsLibraryVersion) {
      console.info(
        `%cMobile worker component Library Version: ${config.environment?.componentsLibraryVersion}`,
        'color: #f08900; font-weight: bold;'
      );
    }

    if (config.environment?.coreLibraryVersion) {
      console.info(
        `%cMobile worker core Library Version: ${config.environment?.coreLibraryVersion}`,
        'color: #f08900; font-weight: bold;'
      );
    }

    return {
      ngModule: MwCoreModule,
      providers: [
        MwNotificationService,
        MwPlatformService,
        MwLocaleService,
        MwIconLoaderService,
        MwAppConfigService,
        MwCoreDateTimeService,
        {
          provide: FIREBASE_OPTIONS,
          useValue: config.environment.firebase || {},
        },
        { provide: FIREBASE_APP_NAME, useValue: '[Default]' },
        {
          provide: LOCALE_ID,
          useFactory: localeFactory,
          deps: [MwLocaleService],
        },
        {
          provide: MW_APP_CONFIG,
          useValue: localConfig,
        },
        {
          provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
          useValue: {
            appearance: 'fill',
            floatLabel: localConfig.floatLabel || 'always',
          },
        },
      ],
    };
  }
}
